import { useCallback } from "react";

import type {
  DragEndEvent,
  SortableDivider,
} from "metabase/common/components/Sortable";
import { Sortable, SortableList } from "metabase/common/components/Sortable";
import { useDndSensors } from "metabase/common/hooks";
import type { IconProps } from "metabase/ui";
import type { AccentColorOptions } from "metabase/ui/colors/types";
import type { ChartSettingOrderedItem } from "metabase/viz-core";

import { ColumnItem } from "../ColumnItem";

interface SortableColumnFunctions<T> {
  onRemove?: (item: T) => void;
  onEdit?: (item: T, targetElement: HTMLElement) => void;
  onClick?: (item: T) => void;
  onAdd?: (item: T) => void;
  onEnable?: (item: T) => void;
  getItemName: (item: T) => string;
  onColorChange?: (item: T, hexValue: string, colorName?: string) => void;
}
interface ChartSettingOrderedItemsProps<
  T extends ChartSettingOrderedItem,
> extends SortableColumnFunctions<T> {
  onSortEnd: ({ id, newIndex }: DragEndEvent) => void;
  items: T[];
  getId: (item: T) => string | number;
  isSortable?: boolean;
  removeIcon?: IconProps["name"];
  accentColorOptions?: AccentColorOptions;
  getItemColor?: (item: ChartSettingOrderedItem) => string | undefined;
  dividers?: SortableDivider[];
}

export function ChartSettingOrderedItems<T extends ChartSettingOrderedItem>({
  onRemove,
  onSortEnd,
  onEdit,
  onAdd,
  onEnable,
  onClick,
  getItemName,
  items,
  onColorChange,
  getId,
  isSortable = true,
  removeIcon,
  accentColorOptions,
  getItemColor = (item) => item.color,
  dividers = [],
}: ChartSettingOrderedItemsProps<T>) {
  const isDragDisabled = !isSortable || items.length < 2;
  const sensors = useDndSensors({ distance: 15 });

  const renderItem = useCallback(
    ({ item, id }: { item: T; id: string | number }) =>
      !item.isOther ? (
        <Sortable
          id={id}
          key={`sortable-${id}`}
          disabled={isDragDisabled}
          draggingStyle={{ opacity: 0.5 }}
        >
          <ColumnItem
            title={getItemName(item)}
            onEdit={
              onEdit && !item.hideSettings
                ? (targetElement: HTMLElement) => onEdit(item, targetElement)
                : undefined
            }
            onRemove={
              onRemove && item.enabled ? () => onRemove(item) : undefined
            }
            onClick={onClick ? () => onClick(item) : undefined}
            onAdd={onAdd ? () => onAdd(item) : undefined}
            onEnable={
              onEnable && !item.enabled ? () => onEnable(item) : undefined
            }
            onColorChange={
              onColorChange
                ? (hexValue: string, colorName?: string) =>
                    onColorChange(item, hexValue, colorName)
                : undefined
            }
            color={getItemColor(item)}
            draggable={!isDragDisabled}
            icon={item.icon}
            removeIcon={removeIcon}
            role="listitem"
            accentColorOptions={accentColorOptions}
          />
        </Sortable>
      ) : null,
    [
      isDragDisabled,
      removeIcon,
      getItemName,
      onEdit,
      onRemove,
      onClick,
      onAdd,
      onEnable,
      onColorChange,
      accentColorOptions,
      getItemColor,
    ],
  );

  return (
    <SortableList
      getId={getId}
      renderItem={renderItem}
      items={items}
      onSortEnd={onSortEnd}
      sensors={sensors}
      dividers={dividers}
    />
  );
}
